import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const GoogleButton = ({ next }) => (
  <button
    type="button"
    className="btn-secondary w-full !bg-white"
    data-testid="google-login-button"
    onClick={() => {
      sessionStorage.setItem("v2r_next", next);
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/orders";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
  >
    <img src="https://www.google.com/favicon.ico" alt="" className="h-5 w-5" /> Continue with Google
  </button>
);

const EmailCode = () => {
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const send = (e) => { e?.preventDefault(); run(async () => {
    await api.post("/auth/email/request", { email: email.trim() });
    setSent(true); setCode(""); toast.success(`We emailed a 6-digit code to ${email.trim()}`);
  }); };
  const verify = (e) => { e.preventDefault(); run(async () => {
    const { data } = await api.post("/auth/email/verify", { email: email.trim(), code });
    setUser(data);
  }); };
  if (!sent) return (
    <form onSubmit={send} className="space-y-3" data-testid="email-code-request-form">
      <label className="block text-lg font-semibold text-[#4A2A18]" htmlFor="login-email">Or use any email address</label>
      <input id="login-email" type="email" required autoComplete="email" className="field w-full" placeholder="you@example.com"
        value={email} onChange={(e) => setEmail(e.target.value)} data-testid="email-code-email-input" />
      <button className="btn-primary w-full" disabled={busy || !email.trim()} data-testid="email-code-send-button">{busy ? "Sending..." : "Email me a sign-in code"}</button>
    </form>
  );
  return (
    <form onSubmit={verify} className="space-y-3" data-testid="email-code-verify-form">
      <p className="text-lg text-[#4A2A18]" data-testid="email-code-sent-note">We sent a 6-digit code to <b className="break-all">{email.trim()}</b>. It works for 10 minutes. Check your spam folder if you don't see it.</p>
      <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} className="field w-full text-center text-3xl tracking-[0.5em]" placeholder="------"
        value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="email-code-input" aria-label="6-digit code" />
      <button className="btn-primary w-full" disabled={busy || code.length !== 6} data-testid="email-code-verify-button">{busy ? "Checking..." : "Sign in"}</button>
      <div className="flex flex-wrap justify-between gap-2 text-base">
        <button type="button" className="text-[#8B0A1A] underline" onClick={send} disabled={busy} data-testid="email-code-resend-button">Send a new code</button>
        <button type="button" className="text-[#5C4A3E] underline" onClick={() => setSent(false)} data-testid="email-code-change-email-button">Use a different email</button>
      </div>
    </form>
  );
};

export default function Login() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const next = params.get("next") || "/create";
  useEffect(() => {
    if (params.get("error")) toast.error("Google sign-in didn't finish. Please try again.");
  }, [params]);

  if (user) return <Navigate to={user.role === "admin" && next === "/create" ? "/admin" : next} replace />;

  return (
    <div className="mx-auto max-w-lg px-4 py-12 sm:py-16" data-testid="login-page">
      <div className="paper-card relative p-6 sm:p-10">
        <span className="tape -top-3 right-8 rotate-[6deg]" />
        <h1 className="text-4xl font-bold text-[#4A2A18]">Sign in or create your account</h1>
        <p className="mt-3 text-lg text-[#5C4A3E]">Use your Google account or any email address. It's the same whether you're new or coming back, and it saves your progress so you can finish your keepsake any time.</p>
        <div className="mt-8"><GoogleButton next={next} /></div>
        <div className="my-6 flex items-center gap-3 text-base text-[#8C7A6B]"><span className="h-px flex-1 bg-[#E6DEC6]" />or<span className="h-px flex-1 bg-[#E6DEC6]" /></div>
        <EmailCode />
        <p className="mt-6 text-base text-[#5C4A3E]" data-testid="login-google-note">No passwords to remember. After signing in, we'll ask for your phone number once so we can reach you about your order.</p>
      </div>
    </div>
  );
}
